/**
 * Professional Product Card Component
 * Reusable card for all marketplace categories
 */

import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Badge,
  Rating,
  Tooltip,
  Avatar,
  Skeleton
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  ShoppingCart,
  Event,
  LocationOn,
  Verified,
  LocalShipping,
  CompareArrows,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDistance } from '../../../utils/helpers';

const ProductCard = ({ 
  product, 
  category, 
  onAddToCart, 
  onAddToWishlist, 
  onCompare,
  isInWishlist = false,
  isInComparison = false,
  loading = false 
}) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  if (loading) {
    return (
      <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
        <Skeleton variant="rectangular" height={200} />
        <CardContent sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" height={28} />
          <Skeleton variant="text" height={20} />
          <Skeleton variant="text" height={20} width="60%" />
        </CardContent>
        <Skeleton variant="rectangular" height={60} />
      </Card>
    );
  }

  const handleCardClick = () => {
    navigate(`/marketplace/${category}/${product.id}`);
  };

  const handleAction = (e, action) => {
    e.stopPropagation();
    action();
  };

  const getPrimaryAction = () => {
    switch (category) {
      case 'materials':
        return { text: 'Add to Cart', icon: <ShoppingCart />, action: () => onAddToCart(product) };
      case 'equipment':
        return { text: 'Book Now', icon: <Event />, action: () => navigate(`/marketplace/equipment/${product.id}/book`) };
      case 'professionals':
        return { text: 'Hire Now', icon: <Event />, action: () => navigate(`/marketplace/professionals/${product.id}/hire`) };
      case 'insurance':
        return { text: 'Get Quote', icon: <Event />, action: () => navigate(`/marketplace/insurance/${product.id}/quote`) };
      case 'inspectors':
        return { text: 'Schedule', icon: <Event />, action: () => navigate(`/marketplace/inspectors/${product.id}/schedule`) };
      default:
        return { text: 'View Details', icon: <Visibility />, action: handleCardClick };
    }
  };

  const primaryAction = getPrimaryAction();

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
      }}
      onClick={handleCardClick}
    >
      {/* Image Section */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          height="200"
          image={product.images?.[0] || '/api/placeholder/300/200'}
          alt={product.name}
          onLoad={() => setImageLoaded(true)}
          sx={{
            transition: 'transform 0.3s ease',
            '&:hover': { transform: 'scale(1.05)' }
          }}
        />
        
        {/* Overlay Actions */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          <Tooltip title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}>
            <IconButton
              size="small"
              onClick={(e) => handleAction(e, () => onAddToWishlist(product))}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
              }}
            >
              {isInWishlist ? <Favorite color="error" /> : <FavoriteBorder />}
            </IconButton>
          </Tooltip>
          
          <Tooltip title={isInComparison ? 'Remove from comparison' : 'Add to comparison'}>
            <IconButton
              size="small"
              onClick={(e) => handleAction(e, () => onCompare(product))}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
              }}
            >
              <CompareArrows color={isInComparison ? 'primary' : 'inherit'} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Badges */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}>
          {product.isVerified && (
            <Chip
              icon={<Verified />}
              label="Verified"
              size="small"
              color="success"
              sx={{ bgcolor: 'rgba(76, 175, 80, 0.9)' }}
            />
          )}
          
          {product.isFeatured && (
            <Chip
              label="Featured"
              size="small"
              color="warning"
              sx={{ bgcolor: 'rgba(255, 152, 0, 0.9)' }}
            />
          )}

          {product.discount && (
            <Chip
              label={`${product.discount}% OFF`}
              size="small"
              color="error"
              sx={{ bgcolor: 'rgba(244, 67, 54, 0.9)' }}
            />
          )}

          {product.availability === 'limited' && (
            <Chip
              label="Limited Stock"
              size="small"
              sx={{ bgcolor: 'rgba(255, 193, 7, 0.9)' }}
            />
          )}
        </Box>
      </Box>

      {/* Content Section */}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Vendor Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar
            src={product.vendor?.logo}
            alt={product.vendor?.name}
            sx={{ width: 24, height: 24, mr: 1 }}
          >
            {product.vendor?.name?.[0]}
          </Avatar>
          <Typography variant="caption" color="text.secondary">
            {product.vendor?.name}
          </Typography>
          {product.vendor?.isVerified && (
            <Verified color="primary" sx={{ ml: 0.5, fontSize: 14 }} />
          )}
        </Box>

        {/* Product Name */}
        <Typography 
          variant="h6" 
          component="h3" 
          sx={{ 
            mb: 1,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '48px'
          }}
        >
          {product.name}
        </Typography>

        {/* Rating and Reviews */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Rating value={product.rating || 0} precision={0.1} size="small" readOnly />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({product.reviewCount || 0} reviews)
          </Typography>
        </Box>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LocationOn sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {product.location} • {formatDistance(product.distance)}
          </Typography>
        </Box>

        {/* Category-specific info */}
        {category === 'materials' && product.unit && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Per {product.unit} • Min order: {product.minOrder} {product.unit}s
          </Typography>
        )}

        {category === 'equipment' && product.dailyRate && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Daily rate • Available {product.availability}
          </Typography>
        )}

        {category === 'professionals' && product.hourlyRate && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {product.experience} years exp • {product.projectsCompleted}+ projects
          </Typography>
        )}

        {/* Delivery/Service Info */}
        {product.freeDelivery && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocalShipping sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
              Free Delivery
            </Typography>
          </Box>
        )}

        {/* Specializations/Tags */}
        {product.specializations && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {product.specializations.slice(0, 2).map((spec, index) => (
              <Chip key={index} label={spec} size="small" variant="outlined" />
            ))}
            {product.specializations.length > 2 && (
              <Chip label={`+${product.specializations.length - 2}`} size="small" variant="outlined" />
            )}
          </Box>
        )}
      </CardContent>

      {/* Actions Section */}
      <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
            {formatCurrency(product.price)}
          </Typography>
          {product.originalPrice && product.originalPrice > product.price && (
            <Typography 
              variant="caption" 
              sx={{ 
                textDecoration: 'line-through',
                color: 'text.secondary'
              }}
            >
              {formatCurrency(product.originalPrice)}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          startIcon={primaryAction.icon}
          onClick={(e) => handleAction(e, primaryAction.action)}
          sx={{ 
            minWidth: 120,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          {primaryAction.text}
        </Button>
      </CardActions>
    </Card>
  );
};

export default ProductCard;